import React from 'react';

import {
  arrivalNoticeLogApis,
  dashboardArrivalNoticeLogApis,
} from '@apis/next/arrivalNoticeLogApis';
import { Layout } from '@components/Layout';
import { PrintableContainer } from '@components/Printable';
import { cellFactory, Table } from '@components/Table/Table';
import { bookingsStrings } from '@data/bookings';
import { useSearch } from '@hooks/useSearch';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { formatDateTime, toJST } from '@utils/types';
import * as t from 'io-ts';
import { max } from 'lodash-es';
import * as h from 'tyrann-io';

const useStyles = makeMuiStyles((theme, _common) => ({
  customInner: {
    maxWidth: '65rem',
  },
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  dataNotExist: {
    color: theme.palette.grey[400],
    margin: '3rem auto',
  },
}));

const strings = createStrings({
  ja: {
    dataNotExist: '履歴なし',
    no: 'No',
    updatedAt: '更新時間',
    editor: '編集者',
    editedField: '変更内容',
    beforeValue: '変更前',
    afterValue: '変更後',
    detail: '変更理由',
  },
});

const searchType = t.type({
  id: h.number().cast(),
  isClient: h.boolean().booleanish(),
});

export const PrintArrivalNoticeLogs: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search } = useSearch(searchType);

  const arrivalNoticeLogs = search.isClient
    ? arrivalNoticeLogApis.useGet('/arrival-notice-log/:id', {
        params: {
          id: search.id,
        },
      })
    : dashboardArrivalNoticeLogApis.useGet('/dashboard/arrival-notice-log/:id', {
        params: {
          id: search.id,
        },
      });

  const bodyColumns =
    arrivalNoticeLogs.data?.map((item, index) => {
      return [
        cellFactory.create((index + 1).toString()),
        cellFactory.create(item.editor || '-'),
        cellFactory.create(formatDateTime(item.updated_at)),
        cellFactory.create(item.label || '-'),
        cellFactory.create(item.before_value || '-'),
        cellFactory.create(item.after_value || '-'),
        cellFactory.create(item.detail || '-'),
      ];
    }) ?? [];

  let contents: React.ReactNode = null;
  if (arrivalNoticeLogs.data && arrivalNoticeLogs.data?.length > 0) {
    contents = (
      <Table>
        <Table.Head>
          <Table.Row
            columns={[
              cellFactory.create(strings.no || '-'),
              cellFactory.create(strings.editor || '-'),
              cellFactory.create(strings.updatedAt || '-'),
              cellFactory.create(strings.editedField || '-'),
              cellFactory.create(strings.beforeValue || '-'),
              cellFactory.create(strings.afterValue || '-'),
              cellFactory.create(strings.detail || '-'),
            ]}
          />
        </Table.Head>
        <Table.Body>
          {bodyColumns.map((columns) => {
            return <Table.Row columns={columns} />;
          })}
        </Table.Body>
      </Table>
    );
  } else if (arrivalNoticeLogs.data && arrivalNoticeLogs.data?.length === 0) {
    contents = (
      <>
        <Table>
          <Table.Head>
            <Table.Row
              columns={[
                cellFactory.create(strings.no || '-'),
                cellFactory.create(strings.editor || '-'),
                cellFactory.create(strings.updatedAt || '-'),
                cellFactory.create(strings.editedField || '-'),
                cellFactory.create(strings.beforeValue || '-'),
                cellFactory.create(strings.afterValue || '-'),
                cellFactory.create(strings.detail || '-'),
              ]}
            />
          </Table.Head>
        </Table>
        <p className={classes.dataNotExist}>{strings.dataNotExist}</p>
      </>
    );
  }

  const updatedAt = arrivalNoticeLogs.data && max(arrivalNoticeLogs.data.map((d) => d.updated_at));

  return (
    <Layout
      hideHeader
      hideFooter
      hideCookiePolicy
      loading={arrivalNoticeLogs.isLoading}
      title={bookingsStrings.currentBookings}
      errors={[arrivalNoticeLogs.error]}
      classes={{
        background: classes.background,
      }}
    >
      <PrintableContainer classes={{ inner: classes.customInner }}>
        {updatedAt && (
          <p className={classes.horizontalSpace1}>
            <span>Latest Update:</span>
            <span>{updatedAt ? toJST(new Date(updatedAt)) : '-'}</span>
          </p>
        )}
        {contents}
      </PrintableContainer>
    </Layout>
  );
};
